import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Undo2 } from "lucide-react";

/**
 * Dialog modal pentru preluarea PSA de la un angajat. Purely presentational —
 * toate datele + handlerii vin prin props.
 */
export default function PSAPickupDialog({
  open,
  busy,
  personId,
  personName,
  assigned,
  persons,
  locations,
  pickupTargets,
  bulkDest,
  pickupDate,
  pickupBy,
  PSACategoryIcon,
  onOpenChange,
  onSetBulkDest,
  onSetPickupDate,
  onSetPickupBy,
  onApplyBulk,
  onSelectAll,
  onUpdateRow,
  onSubmit,
  onCancel,
}) {
  const selectedCount = Object.values(pickupTargets).filter((v) => v?.selected).length;
  const withDestCount = Object.values(pickupTargets).filter((v) => v?.selected && v?.dest).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="psa-pickup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 size={18} className="text-orange-700"/> Preia PSA de la {personName}
          </DialogTitle>
        </DialogHeader>

        {/* Bulk action bar */}
        <div className="bg-orange-50 border-2 border-orange-300 rounded-md p-2.5 space-y-2" data-testid="psa-pickup-bulk-bar">
          <div className="text-xs font-bold uppercase tracking-wider text-orange-900">⚡ Acțiuni rapide</div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => onSelectAll(true)} className="h-7 text-xs" data-testid="psa-pickup-select-all">
              ☑ Selectează toate
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => onSelectAll(false)} className="h-7 text-xs" data-testid="psa-pickup-clear-all">
              ☐ Deselectează tot
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">
                Destinație comună pentru articolele selectate
              </Label>
              <Select value={bulkDest || "__none__"} onValueChange={(v) => onSetBulkDest(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="psa-pickup-bulk-dest">
                  <SelectValue placeholder="— Alege locație SAU persoană —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Niciuna —</SelectItem>
                  <SelectItem value="__sep_loc__" disabled className="font-bold text-blue-900 bg-blue-50">📦 Locații</SelectItem>
                  {locations.map((loc) => (<SelectItem key={`loc-${loc}`} value={`loc:${loc}`}>📍 {loc}</SelectItem>))}
                  <SelectItem value="__sep_per__" disabled className="font-bold text-purple-900 bg-purple-50 mt-1">👤 Persoane</SelectItem>
                  {persons.filter((p) => p.id !== personId).map((p) => (
                    <SelectItem key={`per-${p.id}`} value={`person:${p.id}`}>{p.nume} {p.prenume}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" size="sm" onClick={onApplyBulk} disabled={!bulkDest} className="h-8 text-xs bg-orange-600 hover:bg-orange-700" data-testid="psa-pickup-bulk-apply">
              Aplică pe toate selectate
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">
                📅 Data preluării
              </Label>
              <input
                type="date"
                value={pickupDate || ""}
                onChange={(e) => onSetPickupDate(e.target.value)}
                className="block h-8 px-2 text-xs border border-orange-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                data-testid="psa-pickup-date"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] text-zinc-600 font-bold uppercase tracking-wider">
                👤 Preluat de
              </Label>
              <Select value={pickupBy || "__none__"} onValueChange={(v) => onSetPickupBy(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs border-orange-300" data-testid="psa-pickup-by">
                  <SelectValue placeholder="— Alege persoana —" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Eu (utilizatorul curent) —</SelectItem>
                  {[...persons]
                    .sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`))
                    .map((p) => (
                      <SelectItem key={`pby-${p.id}`} value={p.id}>{p.nume} {p.prenume}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500">
            Data la care se înregistrează preluarea (implicit azi — poate fi setată retroactiv) și persoana care a preluat articolele de la angajat.
          </div>
        </div>

        {/* Lista articolelor cu destinație per rând */}
        {assigned.length === 0 ? (
          <div className="text-xs text-zinc-500 italic py-4 text-center">Niciun PSA de preluat.</div>
        ) : (
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1" data-testid="psa-pickup-list">
            {assigned.map((it) => {
              const row = pickupTargets[it.id] || {};
              return (
                <div
                  key={it.id}
                  className={`p-2 rounded border ${row.selected ? "bg-orange-50/50 border-orange-400" : "bg-white border-zinc-200"}`}
                  data-testid={`psa-pickup-row-${it.id}`}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!row.selected}
                      onChange={(e) => onUpdateRow(it.id, { selected: e.target.checked })}
                      className="w-4 h-4 accent-orange-600 mt-1"
                      data-testid={`psa-pickup-check-${it.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <PSACategoryIcon cat={it.psa_category} size={14} className="text-cyan-700"/>
                        <span className="font-semibold text-sm">{it.name}</span>
                        {it.psa_size && <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold text-[10px]">măs. {it.psa_size}</span>}
                        {it.serial && <span className="font-mono text-[10px] text-zinc-500">SN: {it.serial}</span>}
                        {it.quantity > 1 && <span className="text-[10px] text-zinc-600">{it.quantity}{it.unit ? " " + it.unit : ""}</span>}
                        {it.price_chf != null && (
                          <span className="text-[10px] text-emerald-700 font-semibold">{(parseFloat(it.price_chf) * (parseFloat(it.quantity) || 1)).toFixed(2)} CHF</span>
                        )}
                      </div>
                      {row.selected && (
                        <div className="mt-1.5">
                          <Select value={row.dest || "__none__"} onValueChange={(v) => onUpdateRow(it.id, { dest: v === "__none__" ? "" : v })}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`psa-pickup-dest-${it.id}`}>
                              <SelectValue placeholder="— Destinație —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Alege destinația —</SelectItem>
                              <SelectItem value="__sep_loc__" disabled className="font-bold text-blue-900 bg-blue-50">📦 Locații</SelectItem>
                              {locations.map((loc) => (<SelectItem key={`row-loc-${loc}`} value={`loc:${loc}`}>📍 {loc}</SelectItem>))}
                              <SelectItem value="__sep_per__" disabled className="font-bold text-purple-900 bg-purple-50 mt-1">👤 Persoane</SelectItem>
                              {persons.filter((p) => p.id !== personId).map((p) => (
                                <SelectItem key={`row-per-${p.id}`} value={`person:${p.id}`}>{p.nume} {p.prenume}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2">
          <div className="text-xs text-zinc-600 mr-auto self-center">
            {selectedCount} selectate · {withDestCount} cu destinație
          </div>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Anulează</Button>
          <Button
            onClick={onSubmit}
            disabled={busy || selectedCount === 0 || !pickupDate}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="psa-pickup-submit"
          >
            {busy ? "Se procesează..." : `📥 Preia ${selectedCount} articol${selectedCount === 1 ? "" : "e"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
